import { Component, h, Prop, Host, Element } from '@stencil/core';
import { Nullable } from '../../../global/types';
import { getTemplate } from './template';
import { getWebFontsAsText } from '../../../utils/browser/fonts';

interface TableContent {
  title: string;
  columns: string[];
  rows: Array<TableRow>;
}

interface TableLink {
  id: string;
  url?: string;
  quantity?: number;
}

export type TableRow = [Array<TableLink>, ...Array<string>];

export interface PrintContent {
  linkText: string;
  type: string;
  id: string;
  name?: Nullable<string>;
  family?: Nullable<string>;
  graphEl: () => HTMLRtGraphElement;
  tableContent?: Nullable<TableContent>;
  infoContent?: string;
}

/**
 * Check against a list of browsers that do not support printing of content of
 * an iframe (without surrounding page)
 */
function supportsIframePrint() {
  const { userAgent } = window.navigator;

  // Internet Explorer
  if (userAgent.match(/Trident|MSIE/i)) {
    return false;
  }

  // iOS Chrome
  if (userAgent.match(/CriOS\//)) {
    return false;
  }

  // iOS Firefox
  if (userAgent.match(/FxiOS\//)) {
    return false;
  }

  return true;
}

@Component({
  tag: 'rt-print',
  styleUrl: 'print.css',
  shadow: true,
})
export class Print {
  @Prop() data: Partial<PrintContent>;

  @Element() el!: HTMLElement;

  printingStarted = false;

  iframe: HTMLIFrameElement;

  supportsIframePrint: boolean;

  webFontStyles = '';
  fontFamily;

  componentDidLoad() {
    this.supportsIframePrint = supportsIframePrint();
    this.webFontStyles = getWebFontsAsText();
    this.fontFamily = window
      .getComputedStyle(this.el, null)
      .getPropertyValue('font-family');
  }

  getDescription(): string {
    const { name, family } = this.data;
    const tags = [];
    // eslint-disable-next-line eqeqeq
    if (name != undefined) {
      tags.push(`<p class="product-name">${name}</p>`);
    }
    // eslint-disable-next-line eqeqeq
    if (family != undefined) {
      tags.push(`<p class="product-family">${family}</p>`);
    }
    return tags.join('');
  }

  getTableContent(): string {
    const { title, columns, rows } = this.data.tableContent;

    const firstRow = (data: TableLink[]): string => {
      return data
        .map((item) => {
          const result = [];

          const quantity = item.quantity
            ? `<span class="product-qnt">${item.quantity}x</span>`
            : '';

          if (item.url) {
            result.push(
              `<a class="product-id" href="${item.url}">${quantity}${item.id}</a>`,
            );
          } else {
            result.push(
              `<span class="product-id">${quantity}${item.id}</span>`,
            );
          }

          return result.join('');
        })
        .join('');
    };

    return `
      <table>
        <tr>
          <th>${title}</th>
          ${columns.map((col) => `<th>${col}</th>`).join('')}
        </tr>
        ${rows
    .map(([product, ...data]: TableRow) => {
      return `<tr>
              <td>${firstRow(product)}</td>
              ${data.map((row) => `<td>${row}</td>`).join('')}
            </tr>`;
    })
    .join('')}
      </table>
    `;
  }

  async printNewTab(htmlContent: string) {
    const printWindow = window.open('');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      setTimeout(() => {
        printWindow.close();
      }, 350);
    }, 500);
  }

  async printIframe(htmlContent: string) {
    this.iframe.contentWindow.document.write(htmlContent);
    this.iframe.contentWindow.document.close();
    setTimeout(() => {
      this.iframe.contentWindow.print();
    }, 50);
  }

  async print() {
    const dataUrl = this.data.graphEl()
      ? await this.data.graphEl().getSnapshot({
        encoderOptions: 1.0,
      })
      : null;

    const htmlContent = getTemplate({
      title: this.data.linkText,
      header: `<h1>${this.data.type}: ${this.data.id}</h1>`,
      description: this.getDescription(),
      graphImage: dataUrl
        ? `<img class="graph-img" alt="${this.data.type}" src="${dataUrl}"/>`
        : '',
      table: this.data.tableContent ? this.getTableContent() : '',
      info:
        this.data.infoContent && dataUrl
          ? `<div class="info">${this.data.infoContent}</div>`
          : '',
      webFontsStyles: this.webFontStyles,
      hostFontFamily: this.fontFamily,
    });

    if (this.supportsIframePrint) {
      this.printIframe(htmlContent);
    } else {
      this.printNewTab(htmlContent);
    }
  }

  render() {
    if (!window.print) {
      return null;
    }
    return (
      <Host>
        <qds-standalone-link
          href="#"
          size="small"
          importance="standard"
          onClick={async (e) => {
            e.preventDefault();
            if (!this.printingStarted) {
              this.printingStarted = true;
              setTimeout(() => {
                // arbitrary interval to prevent multiple print calls on multiple clicks at once
                this.printingStarted = false;
              }, 500);
              this.print();
            }
          }}
          iconName="printer"
        >
          {this.data.linkText}
        </qds-standalone-link>
        <iframe ref={(el) => (this.iframe = el)}></iframe>
      </Host>
    );
  }
}
